import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../lib/api';
import {
  MessageSquare, X, Send, Sparkles, Loader2, Trash2,
  FileText, Mail, UserCheck, TrendingUp, RefreshCw,
  ChevronDown, Bot, Zap
} from 'lucide-react';

const QUICK_ACTIONS = [
  { label: 'Što je novo?', icon: Zap, prompt: 'Što je novo danas? Daj mi sažetak promjena i preporuke.' },
  { label: 'Preporuči leadove', icon: TrendingUp, prompt: 'Preporuči mi 5 firmi za kontakt danas. Objasni zašto svaku.' },
  { label: 'Jutarnji izvještaj', icon: FileText, prompt: '/izvjestaj' },
  { label: 'Savjeti', icon: Sparkles, prompt: 'Daj mi savjete za bolji rad danas na temelju mog pipeline-a i zadataka.' },
];

export default function AIChatWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [hasUnread, setHasUnread] = useState(false);
  const messagesEnd = useRef(null);
  const inputRef = useRef(null);

  // Determine page context
  const getPageContext = () => {
    const path = location.pathname;
    if (path.startsWith('/leads/')) return 'employer';
    if (path === '/leads') return 'leads';
    if (path === '/crm') return 'crm';
    if (path === '/admin') return 'admin';
    return 'dashboard';
  };

  // Generate session ID per user per day
  useEffect(() => {
    if (user) {
      const sid = `${user.id}_${new Date().toISOString().split('T')[0]}`;
      setSessionId(sid);
      // Load history
      api.get('/ai/chat/history', { params: { session_id: sid } })
        .then(res => { if (res.data?.length) setMessages(res.data.map(m => ({ role: m.role, content: m.content }))); })
        .catch(() => {});
    }
  }, [user]);

  // Scroll to bottom
  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Focus input when opened
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 200); }, [open]);

  // Check for daily report
  useEffect(() => {
    if (user && !open) {
      api.get('/ai/daily-report')
        .then(res => { if (!res.data?.content) setHasUnread(true); })
        .catch(() => {});
    }
  }, [user, open]);

  const sendMessage = useCallback(async (text) => {
    if (!text?.trim() || loading) return;

    const userMsg = text.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      // Special command: generate daily report
      if (userMsg === '/izvjestaj') {
        const res = await api.post('/ai/daily-report/generate');
        setMessages(prev => [...prev, { role: 'assistant', content: res.data.content }]);
      } else {
        const res = await api.post('/ai/chat', {
          message: userMsg,
          page: getPageContext(),
          session_id: sessionId,
          country: localStorage.getItem('leo-lead-country') || 'HR',
        });
        setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Greška u komunikaciji. Pokušajte ponovo.' }]);
    } finally {
      setLoading(false);
    }
  }, [loading, sessionId, location.pathname]);

  const clearChat = () => {
    setMessages([]);
    const sid = `${user?.id}_${Date.now()}`;
    setSessionId(sid);
  };

  if (!user) return null;

  return (
    <>
      {/* ═══ FLOATING BUTTON ═══ */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => { setOpen(true); setHasUnread(false); }}
            className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 w-12 h-12 md:w-14 md:h-14 rounded-2xl gradient-gold text-white shadow-xl glow-gold flex items-center justify-center hover:scale-105 transition-transform"
          >
            <Bot size={24} />
            {hasUnread && (
              <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 border-2 border-white animate-pulse" />
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* ═══ CHAT PANEL ═══ */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-0 right-0 md:bottom-6 md:right-6 z-50 w-full md:w-[420px] h-[85vh] md:h-[600px] bg-white md:rounded-2xl shadow-2xl border-2 border-stone-200 flex flex-col overflow-hidden ai-chat-panel"
          >
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between shrink-0" style={{ background: 'linear-gradient(135deg, #1C1917 0%, #292524 100%)' }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl gradient-gold flex items-center justify-center">
                  <Bot size={18} className="text-white" />
                </div>
                <div>
                  <div className="text-white font-bold text-[14px]">LEO AI Asistent</div>
                  <div className="text-white/40 text-[11px]">Personalizirano za {user?.display_name}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={clearChat} title="Novi razgovor"
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/10 transition-all">
                  <RefreshCw size={14} />
                </button>
                <button onClick={() => setOpen(false)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/10 transition-all">
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-auto p-4 space-y-3 bg-stone-50/50">
              {messages.length === 0 && (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-3">
                    <Sparkles size={24} className="text-amber-500" />
                  </div>
                  <h3 className="font-bold text-[15px] mb-1">Pozdrav, {user?.display_name}!</h3>
                  <p className="text-stone-400 text-[13px] mb-5">Kako vam mogu pomoći danas?</p>

                  {/* Quick actions */}
                  <div className="grid grid-cols-2 gap-2">
                    {QUICK_ACTIONS.map(qa => (
                      <button key={qa.label} onClick={() => sendMessage(qa.prompt)}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-left text-[12px] font-semibold bg-white border-2 border-stone-200 text-stone-600 hover:border-amber-300 hover:text-amber-700 transition-all">
                        <qa.icon size={14} className="shrink-0 text-amber-500" />
                        {qa.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className={cn("flex", msg.role === 'user' ? "justify-end" : "justify-start")}>
                  <div className={cn("max-w-[85%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed",
                    msg.role === 'user'
                      ? "bg-stone-900 text-white rounded-br-md"
                      : "bg-white border border-stone-200 text-stone-700 rounded-bl-md shadow-sm")}>
                    {msg.role === 'assistant' ? (
                      <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{
                        __html: msg.content
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\n/g, '<br/>')
                      }} />
                    ) : msg.content}
                  </div>
                </motion.div>
              ))}

              {loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                  <div className="bg-white border border-stone-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2 text-stone-400 text-[13px]">
                      <Loader2 size={14} className="animate-spin" /> Razmišljam...
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEnd} />
            </div>

            {/* Context indicator */}
            <div className="px-4 py-1.5 bg-stone-50 border-t border-stone-100 text-[10px] font-bold text-stone-400 uppercase tracking-wider">
              Kontekst: {getPageContext() === 'employer' ? 'Detalji poslodavca' : getPageContext() === 'leads' ? 'Lead Intelligence' : getPageContext() === 'crm' ? 'CRM' : 'Dashboard'}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-stone-200 bg-white shrink-0">
              <div className="flex items-center gap-2">
                <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
                  placeholder="Pitajte LEO asistenta..."
                  className="flex-1 h-10 px-4 rounded-xl border-2 border-stone-200 text-[13px] focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 outline-none" />
                <button onClick={() => sendMessage(input)} disabled={loading || !input.trim()}
                  className="w-10 h-10 rounded-xl gradient-gold text-white flex items-center justify-center hover:opacity-90 disabled:opacity-40 shadow-md transition-all">
                  <Send size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
